if (!customElements.get('cp-sticky-subscription')) {
  customElements.define('cp-sticky-subscription', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-cpbar-config]').textContent);
      this.panel = this.querySelector('[data-cpbar-panel]');
      this.inert = this.hidden;
      this.trigger = this.querySelector('[data-cpbar-trigger]');
      this.menu = this.querySelector('[data-cpbar-menu]');
      this.list = this.querySelector('[data-cpbar-options]');
      this.button = this.querySelector('[data-cpbar-add]');
      this.titleElement = this.querySelector('[data-cpbar-title]');
      this.saving = this.querySelector('[data-cpbar-saving]');
      this.serving = this.querySelector('[data-cpbar-serving]');
      this.daily = this.querySelector('[data-cpbar-daily]');
      this.error = this.querySelector('[data-cpbar-error]');
      this.spacer = document.createElement('div');
      this.spacer.setAttribute('aria-hidden', 'true');
      this.spacer.className = 'cp-sticky-subscription-spacer';
      document.body.append(this.spacer);
      const signal = this.controller.signal;
      if (this.dataset.visibility === 'after_button') {
        const scheduleVisibility = () => {
          if (this.visibilityFrame) return;
          this.visibilityFrame = requestAnimationFrame(() => {
            this.visibilityFrame = null;
            this.updateVisibility();
          });
        };
        window.addEventListener('scroll', scheduleVisibility, { passive: true, signal });
        window.addEventListener('resize', scheduleVisibility, { passive: true, signal });
        window.addEventListener('pageshow', scheduleVisibility, { signal });
        window.visualViewport?.addEventListener('resize', scheduleVisibility, { passive: true, signal });
      }
      document.addEventListener('cp:subscription:change', (event) => {
        if (event.target !== this.findWidget()) return;
        this.bindWidget(event.target);
        this.sync(event.detail);
      }, { signal });
      document.addEventListener('shopify:section:load', () => this.connectWidget(), { signal });
      document.addEventListener('shopify:section:unload', () => queueMicrotask(() => {
        if (this.isConnected) this.connectWidget();
      }), { signal });
      this.trigger.addEventListener('click', () => this.menu.hidden ? this.openMenu() : this.closeMenu(), { signal });
      this.trigger.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
      this.trigger.addEventListener('blur', () => this.closeMenu(), { signal });
      this.list.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[data-cpbar-option]');
        if (!option || option.getAttribute('aria-disabled') === 'true') return;
        this.chooseOffer(option.dataset.plan);
        this.closeMenu(true);
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.querySelector('.cpbar__offer').contains(event.target)) this.closeMenu();
      }, { signal });
      this.button.addEventListener('click', () => this.addToCart(), { signal });
      this.resizeObserver = new ResizeObserver(entries => {
        const entry=entries.find(item=>item.target===this.panel);
        const box=entry?.borderBoxSize;
        this.panelHeight=Array.isArray(box)?box[0]?.blockSize:box?.blockSize;
        this.reserveSpace();
      });
      this.resizeObserver.observe(this.panel, { box: 'border-box' });
      this.connectWidget();
      customElements.whenDefined('cp-subscription-widget-v2').then(() => {
        if (this.isConnected && this.controller) this.connectWidget();
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null;
      this.sourceObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.spacer?.remove();
      this.widget = null;
      this.state = null;
      this.optionsSignature = null;
    }
    findWidget() {
      return document.querySelector(`product-info[data-product-id="${this.dataset.productId}"] cp-subscription-widget-v2`);
    }
    connectWidget() {
      const widget = this.findWidget();
      if (!widget?.purchaseState) {
        this.closeMenu();
        this.inert = true;
        this.hidden = true;
        this.reserveSpace();
        return;
      }
      this.bindWidget(widget);
      this.sync(widget.purchaseState);
    }
    bindWidget(widget) {
      if (this.widget === widget) return;
      this.sourceObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.widget = widget;
      this.optionsSignature = null;
      this.sourceButton = widget.form?.querySelector('[type="submit"]');
      this.sourceObserver = new MutationObserver(() => this.syncSubmitState());
      this.sourceObserver.observe(widget, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden']
      });
      if (this.sourceButton) {
        this.visibilityObserver = new IntersectionObserver(() => this.updateVisibility());
        this.visibilityObserver.observe(this.sourceButton);
      }
    }
    fill(template, values) {
      return template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
    }
    servingLabel(offer) {
      return this.fill(this.config.servingLabel, { price: this.widget.money(offer.servingPrice), currency: this.state.currency });
    }
    makePill(offer) {
      const pill = document.createElement('span');
      pill.className = 'cpbar__save';
      const card = [...this.widget.querySelectorAll('[data-offer]')].find(item => item.dataset.plan === offer.planId);
      const source = card?.querySelector('[data-save]');
      pill.textContent = source?.textContent.trim() || this.fill(this.widget.config.saveText, offer);
      pill.hidden = !offer.saving;
      if (source) {
        const style = getComputedStyle(source);
        for (const property of ['color', 'backgroundColor', 'backgroundImage', 'borderColor', 'borderWidth', 'borderStyle']) pill.style[property] = style[property];
        pill.style.setProperty('--cpbar-save-shine', style.getPropertyValue('--pill-shine').trim() || '#E8FFC5');
        pill.classList.toggle('cpbar__save--shimmer', source.classList.contains('cpv2__save--shimmer'));
      }
      return pill;
    }
    makeOption(offer, index) {
      const option = document.createElement('div');
      option.className = 'cpbar__option';
      option.id = `${this.list.id}-${index}`;
      option.dataset.cpbarOption = '';
      option.dataset.plan = offer.planId;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-disabled', String(!offer.available));
      const copy = document.createElement('span');
      copy.className = 'cpbar__option-copy';
      const row = document.createElement('span');
      row.className = 'cpbar__title-row';
      const title = document.createElement('span');
      title.className = 'cpbar__title';
      title.textContent = this.fill(this.config.offerLabel, offer);
      row.append(title, this.makePill(offer));
      const serving = document.createElement('span');
      serving.className = 'cpbar__serving';
      serving.textContent = this.servingLabel(offer);
      const check = document.createElement('span');
      check.className = 'cpbar__option-check';
      check.setAttribute('aria-hidden', 'true');
      copy.append(row, serving);
      option.append(copy, check);
      return option;
    }
    sync(state) {
      this.state = state;
      const current = state.offers.find(offer => offer.planId === state.selectedPlan);
      const signature = JSON.stringify(state.offers);
      if (signature !== this.optionsSignature) {
        this.optionsSignature = signature;
        this.list.replaceChildren(...state.offers.map((offer, index) => this.makeOption(offer, index)));
      }
      for (const option of this.list.children) option.setAttribute('aria-selected', String(option.dataset.plan === state.selectedPlan));
      if (current) {
        this.titleElement.textContent = this.fill(this.config.offerLabel, current);
        this.saving.replaceChildren(this.makePill(current));
        this.serving.textContent = this.servingLabel(current);
        this.daily.textContent = this.fill(this.config.dailyLabel, { price: this.widget.money(current.monthlyPrice) });
        this.trigger.setAttribute('aria-label', `${this.config.chooseLabel}: ${this.titleElement.textContent}, ${this.saving.textContent}, ${this.serving.textContent}`);
        this.buttonLabel = this.fill(this.config.addLabel, {
          title: current.title, total: this.widget.money(current.total), currency: state.currency, days: current.days
        });
        this.button.title = this.buttonLabel;
      } else {
        this.titleElement.textContent = this.config.unavailableLabel;
        this.saving.replaceChildren();
        this.serving.textContent = '';
        this.daily.textContent = '';
        this.trigger.setAttribute('aria-label', this.config.unavailableLabel);
        this.buttonLabel = this.config.unavailableLabel;
      }
      if (!this.menu.hidden) this.setActive(state.selectedPlan);
      this.syncSubmitState();
      this.updateVisibility();
    }
    syncSubmitState() {
      if (!this.widget?.isConnected || !this.sourceButton) return;
      const busy = this.sourceButton.classList.contains('loading');
      this.button.disabled = !this.state?.ready || busy || this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.button.setAttribute('aria-busy', String(busy));
      this.button.setAttribute('aria-label', busy ? this.config.addingLabel : (this.buttonLabel || this.config.unavailableLabel));
      this.button.classList.toggle('is-loading', busy);
      this.trigger.disabled = busy || !this.state?.offers.some(offer => offer.available);
      if (this.trigger.disabled) this.closeMenu();
      const sourceError = this.widget.querySelector('.cpv2__primary .product-form__error-message-wrapper');
      const message = sourceError && !sourceError.hidden ? sourceError.textContent.trim() : '';
      if (this.error.textContent !== message) this.error.textContent = message;
      this.error.hidden = !message;
    }
    openMenu() {
      if (this.trigger.disabled) return;
      this.menu.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.setActive(this.state.selectedPlan);
    }
    closeMenu(returnFocus = false) {
      if (!this.menu) return;
      this.menu.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      if (returnFocus) this.trigger.focus({ preventScroll: true });
    }
    setActive(planId) {
      const options = [...this.list.children].filter(option => option.getAttribute('aria-disabled') !== 'true');
      const active = options.find(option => option.dataset.plan === planId) || options[0];
      if (!active) return;
      this.activePlan = active.dataset.plan;
      for (const option of this.list.children) option.classList.toggle('is-active', option === active);
      this.trigger.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
    onKeyDown(event) {
      if (this.trigger.disabled) return;
      const { key } = event;
      if (key === 'Escape' && !this.menu.hidden) {
        event.preventDefault();
        this.closeMenu();
      } else if (key === 'Tab' && !this.menu.hidden) {
        this.chooseOffer(this.activePlan);
        this.closeMenu();
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (this.menu.hidden) this.openMenu();
        else {
          this.chooseOffer(this.activePlan);
          this.closeMenu();
        }
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
        event.preventDefault();
        const wasClosed = this.menu.hidden;
        if (wasClosed) this.openMenu();
        const offers = this.state.offers.filter(offer => offer.available);
        let index = offers.findIndex(offer => offer.planId === this.activePlan);
        if (key === 'Home') index = 0;
        else if (key === 'End') index = offers.length - 1;
        else if (!wasClosed) index = Math.max(0, Math.min(offers.length - 1, index + (key === 'ArrowDown' ? 1 : -1)));
        if (offers[index]) this.setActive(offers[index].planId);
      }
    }
    chooseOffer(planId) {
      if (!this.widget?.isConnected) return this.connectWidget();
      const radio = [...this.widget.querySelectorAll('input[name="selling_plan"]')].find(input => input.value === planId);
      if (!radio || radio.disabled) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    addToCart() {
      if (this.button.disabled || !this.widget?.isConnected) return;
      this.closeMenu();
      if (this.widget.update() && !this.sourceButton.disabled && this.sourceButton.getAttribute('aria-disabled') !== 'true') {
        this.widget.form.requestSubmit(this.sourceButton);
      }
    }
    updateVisibility() {
      const afterButton = this.dataset.visibility === 'after_button' && !window.Shopify?.designMode;
      const passedButton = this.sourceButton?.getBoundingClientRect().bottom <= 0;
      const hidden = !this.state?.offers.length || (afterButton && !passedButton);
      if (this.hidden === hidden) return;
      this.inert = hidden;
      this.hidden = hidden;
      if (this.hidden) this.closeMenu();
      this.reserveSpace();
    }
    reserveSpace() {
      if (!this.spacer) return;
      const measured=this.panelHeight>0?this.panelHeight:this.panel.getBoundingClientRect().height;
      const height = `${this.hidden ? 0 : measured}px`;
      if (this.spacer.style.height !== height) this.spacer.style.height = height;
    }
  });
}
